/**
 * useNetworkStatus Hook
 *
 * Monitor network connectivity and provide reconnection handling
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/services/supabase/client';

interface NetworkStatus {
  /** Whether the device is online */
  isOnline: boolean;
  /** Whether we're reconnecting to Supabase */
  isReconnecting: boolean;
  /** Whether Supabase realtime is connected */
  isRealtimeConnected: boolean;
  /** Last time the connection was lost */
  lastDisconnectedAt: Date | null;
  /** Manually trigger reconnection */
  reconnect: () => Promise<void>;
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(true);
  const [lastDisconnectedAt, setLastDisconnectedAt] = useState<Date | null>(null);

  // Handle browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-reconnect when coming back online
      reconnect();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsRealtimeConnected(false);
      setLastDisconnectedAt(new Date());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Monitor Supabase realtime connection
  useEffect(() => {
    // Subscribe to realtime connection status
    const channel = supabase.channel('system');

    channel
      .on('system', { event: 'connected' }, () => {
        setIsRealtimeConnected(true);
        setIsReconnecting(false);
      })
      .on('system', { event: 'disconnected' }, () => {
        setIsRealtimeConnected(false);
        setLastDisconnectedAt(new Date());
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Reconnect function
  const reconnect = useCallback(async () => {
    if (!isOnline || isReconnecting) return;

    setIsReconnecting(true);

    try {
      // Remove all existing channels and re-subscribe
      const channels = supabase.getChannels();
      for (const channel of channels) {
        await supabase.removeChannel(channel);
      }

      // The components will re-subscribe on their next render
      // Just trigger a small delay to let things settle
      await new Promise((resolve) => setTimeout(resolve, 500));

      setIsRealtimeConnected(true);
    } catch (error) {
      console.error('Failed to reconnect:', error);
    } finally {
      setIsReconnecting(false);
    }
  }, [isOnline, isReconnecting]);

  return {
    isOnline,
    isReconnecting,
    isRealtimeConnected,
    lastDisconnectedAt,
    reconnect,
  };
}

/**
 * Network status banner component props
 */
export interface NetworkBannerProps {
  isOnline: boolean;
  isReconnecting: boolean;
  onReconnect?: () => void;
}
